import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Icon, Text } from "@chakra-ui/react";
import { HiChevronLeft, HiChevronRight, HiOutlinePlus } from "react-icons/hi2";
import { c } from "../classroomColors";
import { Chip, StateChip, quietButton } from "./parts";
import SessionBoard from "./SessionBoard";
import { clock, dayLabel, duration, plural, questionsLine, serverTime, stateTint } from "./spaceTokens";

const Segmented = ({ value, options, onChange, label }) => (
  <Flex role="tablist" aria-label={label} display="inline-flex" gap="3px" p="3px" borderRadius="11px" bg={c.surfaceSubtle} border="1px solid" borderColor={c.borderSoft}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <Box
          key={o.value}
          as="button"
          type="button"
          role="tab"
          aria-selected={on}
          onClick={() => onChange(o.value)}
          px="12px"
          py="5px"
          borderRadius="8px"
          fontSize="12.5px"
          fontWeight={on ? "600" : "400"}
          bg={on ? c.surface : "transparent"}
          color={on ? c.text : c.textSub}
          boxShadow={on ? "0 1px 2px rgba(0,0,0,0.06)" : "none"}
          whiteSpace="nowrap"
        >
          {o.label}
          {o.count != null && (
            <Box as="span" ml="6px" color={c.textMuted} fontWeight="400" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {o.count}
            </Box>
          )}
        </Box>
      );
    })}
  </Flex>
);

/** One session in the list: when, what, where it stands, and its next step. */
const Row = ({ session, isInstructor, isDark, isSelected, onOpen }) => {
  const done = session.state === "DONE";
  const extras = [
    session.activity_count ? plural(session.activity_count, "activity", "activities") : null,
    session.assignment_count ? plural(session.assignment_count, "task") : null,
    session.attachment_count ? plural(session.attachment_count, "file") : null,
  ].filter(Boolean);
  return (
    <Flex
      as="button"
      type="button"
      onClick={() => onOpen(session)}
      aria-current={isSelected || undefined}
      textAlign="left"
      w="100%"
      align="center"
      gap="14px"
      p="12px 14px"
      borderRadius="14px"
      bg={c.page}
      border="1px solid"
      borderColor={isSelected ? c.text : c.borderSoft}
      boxShadow={isSelected ? `0 0 0 1px ${c.text}` : "none"}
      transition="border-color 0.15s, background 0.15s"
      _hover={isSelected ? {} : { borderColor: c.textMuted }}
      flexWrap={{ base: "wrap", md: "nowrap" }}
    >
      <Box w={{ base: "100%", md: "104px" }} flexShrink={0} lineHeight="1.3">
        <Text fontSize="13.5px" fontWeight="600" color={c.text}>
          {dayLabel(session.scheduled_at) || "No date yet"}
        </Text>
        <Text fontSize="12px" color={c.textSub}>
          {[clock(session.scheduled_at), duration(session.duration_minutes)].filter(Boolean).join(" · ") || "Draft"}
        </Text>
      </Box>
      <Box flex="1" minW={0}>
        <Text fontSize="14.5px" fontWeight="600" color={c.text} noOfLines={1}>
          {session.name}
        </Text>
        <Text fontSize="12px" color={c.textSub} noOfLines={1}>
          {done
            ? [session.attended_count ? `${plural(session.attended_count, "student")} came` : null, ...extras].filter(Boolean).join(" · ") || "Held"
            : [questionsLine(session, isInstructor), ...extras].join(" · ")}
        </Text>
      </Box>
      <Flex gap="6px" align="center" flexShrink={0}>
        {session.state === "LIVE" && <StateChip state="LIVE" isDark={isDark} />}
        {isInstructor && session.pre_class_unanswered_count > 0 && session.state !== "DONE" && (
          <Chip tone="blue">{session.pre_class_unanswered_count} to answer</Chip>
        )}
        <Text fontSize="12.5px" fontWeight="500" color={c.textSub} px="6px">
          {done ? "Recap" : session.state === "LIVE" ? "Open" : isInstructor ? "Prepare" : "View"} →
        </Text>
      </Flex>
    </Flex>
  );
};

/** Monday of the week `offset` weeks from this one. */
const weekStart = (offset) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
  return d;
};

/** The same sessions laid out across a week, the way a timetable is read. */
const Week = ({ sessions, isDark, onOpen }) => {
  const [offset, setOffset] = useState(0);
  const start = weekStart(offset);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
  const byDay = (d) =>
    sessions
      .filter((s) => {
        if (!s.scheduled_at) return false;
        const t = serverTime(s.scheduled_at);
        return t.getFullYear() === d.getFullYear() && t.getMonth() === d.getMonth() && t.getDate() === d.getDate();
      })
      .sort((a, b) => serverTime(a.scheduled_at) - serverTime(b.scheduled_at));
  const today = new Date().toDateString();

  return (
    <Box>
      <Flex align="center" justify="space-between" mb="10px">
        <Text fontSize="13px" color={c.textSub}>
          {offset === 0 ? "This week" : offset === 1 ? "Next week" : offset === -1 ? "Last week" : `Week of ${dayLabel(start)}`}
        </Text>
        <Flex gap="4px">
          {[
            { label: "Previous week", icon: HiChevronLeft, step: -1 },
            { label: "Next week", icon: HiChevronRight, step: 1 },
          ].map((b) => (
            <Flex key={b.label} as="button" type="button" aria-label={b.label} onClick={() => setOffset((o) => o + b.step)}
              boxSize="30px" borderRadius="9px" align="center" justify="center" bg={c.surface} border="1px solid"
              borderColor={c.borderSoft} color={c.textSub} _hover={{ bg: c.hover, color: c.text }}>
              <Icon as={b.icon} boxSize="15px" />
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Box overflowX="auto" pb="4px">
        <Box display="grid" gridTemplateColumns="repeat(7, minmax(120px, 1fr))" gap="8px" minW="860px">
          {days.map((d) => {
            const isToday = d.toDateString() === today;
            return (
              <Box key={d.toISOString()} minH="180px" p="8px" borderRadius="14px" bg={c.page} border="1px solid"
                borderColor={isToday ? c.text : c.borderSoft}>
                <Text fontSize="12px" fontWeight={isToday ? "600" : "400"} color={isToday ? c.text : c.textSub} mb="6px" px="4px">
                  {dayLabel(d)?.split(" ").slice(0, 2).join(" ")}
                </Text>
                <Flex direction="column" gap="6px">
                  {byDay(d).map((s) => (
                    <Box key={s.guid} as="button" type="button" onClick={() => onOpen(s)} textAlign="left" p="8px 9px"
                      borderRadius="10px" bg={stateTint(s.state, isDark) || c.surface} border="1px solid" borderColor={c.borderSoft}
                      _hover={{ borderColor: c.textMuted }}>
                      <Text fontSize="12px" fontWeight="600" color={c.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {clock(s.scheduled_at)}
                      </Text>
                      <Text fontSize="12px" color={c.text} noOfLines={2}>
                        {s.name}
                      </Text>
                    </Box>
                  ))}
                </Flex>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * A space's sessions, three ways: a list, switched between what is coming,
 * what is still being planned (the teacher's drafts) and what has been
 * held; the same sessions across a week; or a board, a column for each
 * stage. Every session opens in its sheet. Which view is the page's to
 * say (`view`), as the board takes the page's full width.
 */
const SessionList = ({
  groups,
  isInstructor,
  isDark,
  selectedGuid,
  onOpen,
  onCreate,
  view,
  onView,
  studentCount,
  onEnter,
  onSchedule,
  onUnschedule,
}) => {
  const [tab, setTab] = useState("upcoming");
  const upcoming = useMemo(() => [...groups.live, ...groups.upcoming], [groups.live, groups.upcoming]);
  const lists = { upcoming, drafts: groups.drafts, past: groups.past };
  const list = lists[tab] || [];
  const everything = useMemo(
    () => [...groups.live, ...groups.upcoming, ...groups.past],
    [groups.live, groups.upcoming, groups.past]
  );

  const empty = {
    upcoming: isInstructor ? "Nothing coming up. Give a draft a date, or plan a new session." : "Nothing coming up yet.",
    drafts: "No drafts. A session without a date waits here until you schedule it.",
    past: "Sessions you have held show here, each with its recap.",
  };

  return (
    <Box>
      <Flex align="center" justify="space-between" gap="10px" wrap="wrap" mb="12px">
        <Text fontSize="15px" fontWeight="600" color={c.text}>
          Sessions
        </Text>
        <Flex gap="8px" wrap="wrap" align="center">
          {view === "list" && (
            <Segmented
              label="Which sessions"
              value={tab}
              onChange={setTab}
              options={[
                { value: "upcoming", label: "Upcoming", count: upcoming.length },
                ...(isInstructor ? [{ value: "drafts", label: "Drafts", count: groups.drafts.length }] : []),
                { value: "past", label: "Past", count: groups.past.length },
              ]}
            />
          )}
          <Segmented
            label="How to show them"
            value={view}
            onChange={onView}
            options={[
              { value: "board", label: "Board" },
              { value: "list", label: "List" },
              { value: "week", label: "Week" },
            ]}
          />
        </Flex>
      </Flex>

      {view === "board" ? (
        <SessionBoard
          groups={groups}
          isInstructor={isInstructor}
          isDark={isDark}
          selectedGuid={selectedGuid}
          studentCount={studentCount}
          onOpen={onOpen}
          onEnter={onEnter}
          onCreate={onCreate}
          onSchedule={onSchedule}
          onUnschedule={onUnschedule}
          onShowPast={() => {
            setTab("past");
            onView("list");
          }}
        />
      ) : view === "week" ? (
        <Week sessions={everything} isDark={isDark} onOpen={onOpen} />
      ) : list.length ? (
        <Flex direction="column" gap="8px">
          {list.map((s) => (
            <Row key={s.guid} session={s} isInstructor={isInstructor} isDark={isDark} isSelected={selectedGuid === s.guid} onOpen={onOpen} />
          ))}
        </Flex>
      ) : (
        <Flex direction="column" align="center" gap="10px" py="28px" textAlign="center" borderRadius="14px" border="1px dashed" borderColor={c.borderSoft}>
          <Text fontSize="13px" color={c.textSub} maxW="360px">
            {empty[tab]}
          </Text>
          {isInstructor && tab !== "past" && (
            <Button {...quietButton} size="sm" h="34px" borderRadius="10px" fontSize="12.5px" leftIcon={<Icon as={HiOutlinePlus} boxSize="14px" />} onClick={onCreate}>
              New session
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default SessionList;
